import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

export const ResetPasswordForm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { clearRecoveryMode } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);

  // Extraire les paramètres de l'URL (depuis le hash fragment)
  // Supabase redirige avec les tokens dans le hash: #access_token=xxx&refresh_token=yyy&type=recovery
  const getHashParams = () => {
    const hash = window.location.hash.substring(1); // Enlever le #
    const params = new URLSearchParams(hash);
    return {
      access_token: params.get('access_token'),
      refresh_token: params.get('refresh_token'),
      type: params.get('type')
    };
  };

  const hashParams = getHashParams();
  const accessToken = hashParams.access_token || searchParams.get('access_token');
  const refreshToken = hashParams.refresh_token || searchParams.get('refresh_token');
  const type = hashParams.type || searchParams.get('type');

  useEffect(() => {
    const validateToken = async () => {
      if (!accessToken || !refreshToken || type !== 'recovery') {
        toast({
          title: "Lien invalide",
          description: "Ce lien de réinitialisation est invalide ou a expiré",
          variant: "destructive"
        });
        setIsValidating(false);
        return;
      }

      try {
        // Vérifier et définir la session avec les tokens
        const { data, error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        });

        // Vérifier si la session a été établie avec succès
        if (error || !data.session) {
          console.error('Session error:', error);
          toast({
            title: "Lien expiré",
            description: "Ce lien de réinitialisation a expiré. Veuillez en demander un nouveau",
            variant: "destructive"
          });
          setIsValidToken(false);
        } else {
          // Session valide, ne pas afficher de toast d'erreur
          
          setIsValidToken(true);
        }
      } catch (_error) {
        console.error('Validation error:', _error);
        toast({
          title: "Erreur",
          description: "Une erreur s'est produite lors de la validation du lien",
          variant: "destructive"
        });
        setIsValidToken(false);
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [accessToken, refreshToken, type, toast]);

  const validatePassword = (password: string) => {
    if (password.length < 8) {
      return "Le mot de passe doit contenir au moins 8 caractères";
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return "Le mot de passe doit contenir au moins une minuscule";
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return "Le mot de passe doit contenir au moins une majuscule";
    }
    if (!/(?=.*\d)/.test(password)) {
      return "Le mot de passe doit contenir au moins un chiffre";
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs",
        variant: "destructive"
      });
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      toast({
        title: "Mot de passe invalide",
        description: passwordError,
        variant: "destructive"
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: "Erreur",
        description: "Les mots de passe ne correspondent pas",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        toast({
          title: "Erreur",
          description: error.message,
          variant: "destructive"
        });
      } else {
        setResetComplete(true);
        toast({
          title: "Mot de passe mis à jour",
          description: "Votre mot de passe a été mis à jour avec succès"
        });
        
        // Nettoyer le mode recovery
        clearRecoveryMode();
        
        // Déconnecter l'utilisateur pour qu'il se reconnecte avec le nouveau mot de passe
        await supabase.auth.signOut();
        
        // Rediriger vers la page de connexion après 2 secondes
        setTimeout(() => {
          navigate('/auth');
        }, 2000);
      }
    } catch (_error) {
      toast({
        title: "Erreur",
        description: "Une erreur inattendue s'est produite",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // État de validation du token
  if (isValidating) {
    return (
      <div className="w-full">
        <div className="flex items-center mb-8">
          <img
            src="/lovable-uploads/c941c7a1-d271-4c8b-91a4-2611677da8a6.png"
            alt="Getproof Logo"
            className="h-10"
          />
        </div>

        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Validation du lien...
          </h1>
          <p className="text-gray-600">
            Veuillez patienter pendant que nous validons votre lien de réinitialisation
          </p>
        </div>
      </div>
    );
  }

  // Token invalide
  if (!isValidToken) {
    return (
      <div className="w-full">
        <div className="flex items-center mb-8">
          <img
            src="/lovable-uploads/c941c7a1-d271-4c8b-91a4-2611677da8a6.png"
            alt="Getproof Logo"
            className="h-10"
          />
        </div>

        <div className="text-center">
          <div className="flex justify-center mb-6">
            <AlertCircle className="w-16 h-16 text-red-500" />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Lien invalide ou expiré
          </h1>
          <p className="text-gray-600 mb-8">
            Ce lien de réinitialisation n'est plus valide. Veuillez demander un nouveau lien de réinitialisation.
          </p>

          <Button
            onClick={() => navigate('/auth')}
            className="w-full h-12 bg-teal-500 hover:bg-teal-600 text-white font-medium rounded-lg"
          >
            Retour à la connexion
          </Button>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex justify-center space-x-6 text-sm text-gray-500">
            <a href="#" className="hover:text-gray-700">Conditions générales</a>
            <a href="#" className="hover:text-gray-700">Politique de confidentialité</a>
          </div>
        </div>
      </div>
    );
  }

  // Réinitialisation terminée
  if (resetComplete) {
    return (
      <div className="w-full">
        <div className="flex items-center mb-8">
          <img
            src="/lovable-uploads/c941c7a1-d271-4c8b-91a4-2611677da8a6.png"
            alt="Getproof Logo"
            className="h-10"
          />
        </div>

        <div className="text-center">
          <div className="flex justify-center mb-6">
            <CheckCircle className="w-16 h-16 text-teal-500" />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Mot de passe mis à jour !
          </h1>
          <p className="text-gray-600 mb-8">
            Votre mot de passe a été mis à jour avec succès. Vous allez être redirigé vers la page de connexion.
          </p>

          <Button
            onClick={() => navigate('/auth')}
            className="w-full h-12 bg-teal-500 hover:bg-teal-600 text-white font-medium rounded-lg"
          >
            Se connecter maintenant
          </Button>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex justify-center space-x-6 text-sm text-gray-500">
            <a href="#" className="hover:text-gray-700">Conditions générales</a>
            <a href="#" className="hover:text-gray-700">Politique de confidentialité</a>
          </div>
        </div>
      </div>
    );
  }

  // Formulaire de réinitialisation
  return (
    <div className="w-full">
      {/* Logo */}
      <div className="flex items-center mb-8">
        <img
          src="/lovable-uploads/c941c7a1-d271-4c8b-91a4-2611677da8a6.png"
          alt="Getproof Logo"
          className="h-10"
        />
      </div>

      {/* Title */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Créer un nouveau mot de passe
        </h1>
        <p className="text-gray-600">
          Choisissez un mot de passe sécurisé pour votre compte
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <Label htmlFor="password" className="text-sm font-medium text-gray-700 mb-2 block">
            Nouveau mot de passe
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Entrez votre nouveau mot de passe"
              className="pl-10 pr-10 h-12 border-gray-300 focus:border-teal-500 focus:ring-teal-500"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Au moins 8 caractères avec majuscules, minuscules et chiffres
          </p>
        </div>

        <div>
          <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700 mb-2 block">
            Confirmer le mot de passe
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirmez votre nouveau mot de passe"
              className="pl-10 pr-10 h-12 border-gray-300 focus:border-teal-500 focus:ring-teal-500"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <Button
          type="submit"
          className="w-full h-12 bg-teal-500 hover:bg-teal-600 text-white font-medium rounded-lg"
          disabled={isLoading}
        >
          {isLoading ? 'Mise à jour...' : 'Mettre à jour le mot de passe'}
        </Button>

        <div className="text-center pt-4">
          <button
            type="button"
            onClick={() => navigate('/auth')}
            className="text-sm text-teal-600 hover:text-teal-700 hover:underline font-medium"
          >
            ← Retour à la connexion
          </button>
        </div>
      </form>

      {/* Footer links */}
      <div className="mt-12 pt-8 border-t border-gray-200">
        <div className="flex justify-center space-x-6 text-sm text-gray-500">
          <a href="#" className="hover:text-gray-700">Conditions générales</a>
          <a href="#" className="hover:text-gray-700">Politique de confidentialité</a>
        </div>
      </div>
    </div>
  );
};